import React, { useState } from "react";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { getUserRoles } from "../../utils/helpers";

// Console-agnostic sidebar. The nav is supplied by the console that mounts it —
// src/v2/ops/nav-config.js or src/v2/pharmacy/nav-config.js — so this file holds only
// the chrome: collapse state, the role filter, and the NavLink styling.
//
// Each nav entry carries its own `roles`, and every console gates its routes with the
// same list it builds the nav from, so a visible item can never lead somewhere the user
// is bounced from.
function AppSidebar({ nav, brandLabel }) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const userRoles = getUserRoles();
    const items = nav.filter((item) => !item.roles || item.roles.some((role) => userRoles.includes(role)));

    return (
        <aside
            className={`flex shrink-0 flex-col gap-4 border-r border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] py-4 text-[var(--ops-carbon-900)] transition-[width,padding] duration-200 ${
                isCollapsed ? "w-[72px] px-2.5" : "w-[220px] px-4"
            }`}
        >
            <div className="flex items-center gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[linear-gradient(135deg,var(--ops-emerald-600),var(--ops-emerald-500))] font-bold text-[var(--ops-carbon-25)] shadow-[var(--ops-shadow-badge)]">
                    P
                </div>
                {!isCollapsed && (
                    <span className="truncate text-lg font-bold text-[var(--ops-emerald-600)]">
                        {brandLabel}
                    </span>
                )}
                {!isCollapsed && (
                    <button
                        type="button"
                        onClick={() => setIsCollapsed((prev) => !prev)}
                        aria-label="Collapse sidebar"
                        className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] text-lg leading-none text-[var(--ops-carbon-500)] transition-colors hover:border-[var(--ops-emerald-200)] hover:text-[var(--ops-emerald-600)]"
                    >
                        ‹
                    </button>
                )}
            </div>
            {isCollapsed && (
                <button
                    type="button"
                    onClick={() => setIsCollapsed(false)}
                    aria-label="Expand sidebar"
                    className="flex h-7 w-7 items-center justify-center self-center rounded-lg border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] text-lg leading-none text-[var(--ops-carbon-500)] transition-colors hover:border-[var(--ops-emerald-200)] hover:text-[var(--ops-emerald-600)]"
                >
                    ›
                </button>
            )}

            <nav className="flex flex-col gap-1.5">
                {items.map((item) => (
                    <NavLink
                        key={item.key}
                        to={item.path}
                        className={({ isActive }) =>
                            // Legacy sidebar.scss: the ACTIVE item is a solid #28A48B pill with white
                            // text (.navlink-item), and the inactive one is plain black text
                            // (.navlink-item-active — the names are inverted in that file).
                            // The azure hover had no legacy equivalent at all; a neutral tint
                            // replaces it.
                            `group flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 font-sans font-semibold no-underline transition-colors hover:bg-gray-100 ${
                                isCollapsed ? "justify-center px-2" : ""
                            } ${isActive ? "bg-elm-500 text-white-50 hover:bg-elm-500" : "text-black"}`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span
                                    className={// On the active (solid green) pill the icon rides the fill directly —
                                    // a nested chip inside it was the v2 look and reads as a
                                    // second surface on a legacy sidebar.
                                    `flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] transition-colors ${
                                        isActive
                                            ? "bg-transparent text-white-50"
                                            : "bg-gray-100 text-gray-500"
                                    }`}
                                >
                                    <HugeiconsIcon icon={item.icon} size={18} strokeWidth={1.6} />
                                </span>
                                {!isCollapsed && <span className="truncate">{item.label}</span>}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>
        </aside>
    );
}

AppSidebar.propTypes = {
    nav: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
            icon: PropTypes.any.isRequired,
            roles: PropTypes.arrayOf(PropTypes.string),
        })
    ).isRequired,
    brandLabel: PropTypes.string,
};

AppSidebar.defaultProps = {
    brandLabel: "Pillup",
};

export default AppSidebar;
